import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, ShieldCheck, Lock, Users, Layers, Bell,
  HeartHandshake, Home as HomeIcon, Building2, Pill, ClipboardList,
  CalendarDays, Wallet, Activity, GraduationCap, AlertTriangle, FileCheck2,
  UploadCloud, Map as MapIcon, CheckCircle2, PlayCircle, Eye, Rocket,
  UserCog, HandHeart, LineChart, ClipboardCheck, Mail, Sparkles, Server,
} from "lucide-react";
import DemoForm from "@/components/site/DemoForm";
import useSEO from "@/components/site/useSEO";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { BrowserFrame, ManagerDashboard, SCREEN_MAP, SCREEN_IMAGES } from "@/components/site/mockups";

const EASE = [0.16, 1, 0.3, 1];
const IMG = {
  supported: "https://images.unsplash.com/photo-1772724317390-f433f409825a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIweW91bmclMjBhZHVsdHMlMjBzbWlsaW5nJTIwdG9nZXRoZXIlMjBhdCUyMGhvbWV8ZW58MHx8fHwxNzkwNjkzMjgyfDA&ixlib=rb-4.1.0&q=85&w=1200",
  residential: "https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjYXJlZ2l2ZXIlMjBudXJzZSUyMHNlbmlvciUyMHN1cHBvcnR8ZW58MHx8fHwxNzg5MzEzODA3fDA&ixlib=rb-4.1.0&q=85",
  domiciliary: "https://images.unsplash.com/photo-1658314755707-1fbdf7c40145?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxjYXJlZ2l2ZXIlMjBudXJzZSUyMHNlbmlvciUyMHN1cHBvcnR8ZW58MHx8fHwxNzg5MzEzODA3fDA&ixlib=rb-4.1.0&q=85",
};

const Label = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-teal-700 bg-teal-50 px-3 py-1 rounded-full border border-teal-200/60">
    <Sparkles className="w-3 h-3" /> {children}
  </span>
);

/* ============ HERO ============ */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yFrame = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const lines = ["Care Management.", "Connected."];

  return (
    <section ref={ref} id="top" className="relative bg-slate-900 text-white overflow-hidden">
      <div className="absolute inset-0 ayo-grid-bg-dark opacity-60" />
      <motion.div style={{ y: yGlow }} className="absolute -top-40 -right-40 w-[38rem] h-[38rem] rounded-full bg-teal-500/20 blur-[120px]" />
      <motion.div style={{ y: yGlow }} className="absolute top-40 -left-40 w-[32rem] h-[32rem] rounded-full bg-emerald-500/15 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 sm:pt-40 sm:pb-28 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-8 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-teal-300 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Care management platform
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden py-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.15 + i * 0.12 }}
                >
                  {i === 1 ? <span className="bg-gradient-to-r from-teal-300 via-emerald-300 to-cyan-300 bg-clip-text text-transparent">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 text-lg sm:text-xl text-slate-300 max-w-xl leading-relaxed"
          >
            One intelligent platform for safer care, stronger teams and better oversight.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-4 text-base text-slate-400 max-w-xl leading-relaxed"
          >
            AYOBELL connects care planning, medication, workforce management, governance and operational insight in one secure platform built for modern care providers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.72 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a href="#demo" data-testid="hero-book-demo-btn" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 px-7 py-4 rounded-full hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-[1.03] active:scale-[0.98] transition-all">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/platform" data-testid="hero-explore-btn" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-white/5 border border-white/15 px-7 py-4 rounded-full hover:bg-white/10 transition-all">
              Explore the Platform <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-slate-400"
          >
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-teal-400" /> Governance & audit trails</span>
            <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-teal-400" /> Role-based secure access</span>
            <span className="flex items-center gap-1.5"><Layers className="w-4 h-4 text-teal-400" /> Multi-site ready</span>
          </motion.div>
        </div>

        <motion.div style={{ y: yFrame }} className="relative">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 1, ease: EASE, delay: 0.4 }}
          >
            <BrowserFrame image={SCREEN_IMAGES.dashboard?.src} alt={SCREEN_IMAGES.dashboard?.alt}>
              <ManagerDashboard />
            </BrowserFrame>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.1 }}
            className="absolute -bottom-5 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl border border-slate-100 px-4 py-3 flex items-center gap-3"
          >
            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center"><Activity className="w-4 h-4 text-emerald-600" /></div>
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Readiness indicator</p>
              <p className="text-sm font-bold text-slate-900">Readiness indicator: 94%</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ============ SECTOR TRUST STRIP ============ */
const SECTORS = [
  { label: "Supported Living", to: "/supported-living", img: IMG.supported, icon: HeartHandshake, desc: "Supporting independence with better visibility across houses and teams." },
  { label: "Residential Care", to: "/residential-care", img: IMG.residential, icon: Building2, desc: "Connected care, records and oversight across your whole home." },
  { label: "Domiciliary Care", to: "/domiciliary-care", img: IMG.domiciliary, icon: HomeIcon, desc: "From scheduling to care delivery — stay connected in the community." },
];

function SectorStrip() {
  return (
    <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Label>Built for care</Label>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">
            Built for the way you deliver care
          </h2>
        </Reveal>
        <Stagger className="grid md:grid-cols-3 gap-6 mt-14">
          {SECTORS.map((s) => (
            <StaggerItem key={s.label}>
              <Link to={s.to} data-testid={`sector-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="group block rounded-3xl overflow-hidden border border-slate-200 bg-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <div className="relative h-52 overflow-hidden">
                  <img src={s.img} alt={`${s.label} setting`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                    <span className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center"><s.icon className="w-4 h-4" /></span>
                    <span className="font-display font-bold text-lg">{s.label}</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 group-hover:gap-2.5 transition-all">Learn more <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ============ CONNECTED VALUE BAND ============ */
const FLOW = [
  { label: "Care Delivery", icon: ClipboardList },
  { label: "Workforce", icon: CalendarDays },
  { label: "Medication", icon: Pill },
  { label: "Governance", icon: ShieldCheck },
  { label: "Payroll", icon: Wallet },
  { label: "Insight", icon: LineChart },
];
function ValueBand() {
  return (
    <section className="bg-white pb-20 sm:pb-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 text-white px-6 sm:px-10 py-12 sm:py-14">
            <div className="absolute inset-0 ayo-grid-bg-dark opacity-40" />
            <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-teal-500/20 blur-[110px]" />
            <div className="relative">
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
                One platform. From frontline care to management oversight.
              </h2>
              <div className="mt-9 flex flex-wrap items-center gap-x-2 gap-y-4">
                {FLOW.map((f, i) => (
                  <div key={f.label} className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-2 text-sm font-medium bg-white/5 border border-white/10 rounded-full pl-2.5 pr-4 py-2">
                      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-teal-500/15 text-teal-300"><f.icon className="w-3.5 h-3.5" /></span>
                      {f.label}
                    </span>
                    {i < FLOW.length - 1 && <ArrowRight className="w-4 h-4 text-teal-400/70 shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============ MARQUEE ============ */
const CHAPTERS = [
  "01 — Person-centred first",
  "02 — Less admin, more care",
  "03 — Continuous quality & oversight",
  "04 — One connected platform",
];
function Marquee() {
  const row = [...CHAPTERS, ...CHAPTERS];
  return (
    <section className="bg-slate-900 py-8 overflow-hidden ayo-marquee border-y border-white/10">
      <div className="ayo-marquee-track">
        {row.map((c, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-8 font-display text-2xl sm:text-3xl font-bold text-white/90">
            {c}
            <span className="w-2 h-2 rounded-full bg-teal-400" />
          </span>
        ))}
      </div>
    </section>
  );
}

/* ============ PLATFORM OVERVIEW ============ */
const CLUSTERS = [
  { icon: ClipboardList, title: "Care Delivery", items: ["Person-centred care plans", "About Me / Person overview", "Daily notes & observations", "Appointments & goals", "Alerts, risks & consent"] },
  { icon: Pill, title: "Medication", items: ["Medication records", "MAR / eMAR workflows", "PRN medication", "Medication error monitoring"] },
  { icon: CalendarDays, title: "Workforce", items: ["Staff rota & visits", "Attendance", "Leave & holidays", "Training compliance", "Supervisions & appraisals"] },
  { icon: Wallet, title: "Payroll & Staff Hours", badge: "New", highlight: true, items: ["Scheduled, worked & payable hours", "Manager attendance confirmation", "Payroll approval & overtime", "Payroll statements (gross pay)", "CSV / XLSX exports & audit trail"] },
  { icon: ShieldCheck, title: "Governance & Quality", items: ["Incidents & safeguarding", "Complaints & compliments", "Audits & action plans", "CQC readiness dashboard", "Governance reporting"] },
  { icon: Layers, title: "Operations", items: ["Multi-site management", "Manager dashboards", "Appointments calendar", "Site / house filtering", "Notifications"] },
];

function Platform() {
  return (
    <section id="platform" className="bg-slate-50 py-20 sm:py-28 px-4 sm:px-6 lg:px-8 scroll-mt-20 relative">
      <div className="absolute inset-0 ayo-grid-bg opacity-50" />
      <div className="relative max-w-7xl mx-auto">
        <Reveal className="max-w-2xl">
          <Label>The platform</Label>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">
            Everything your care service needs. One connected platform.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Care delivery, people, medication, workforce, governance and operations — organised into the workflows your teams already use.
          </p>
        </Reveal>

        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {CLUSTERS.map((c) => (
            <StaggerItem key={c.title}>
              <div
                data-testid={`platform-cluster-${c.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
                className={`h-full rounded-2xl border p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 ${c.highlight ? "bg-slate-900 border-slate-800 text-white shadow-xl" : "bg-white border-slate-200 hover:shadow-xl hover:border-teal-500/30"}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`flex items-center justify-center w-11 h-11 rounded-xl ${c.highlight ? "bg-white/10 text-teal-300" : "bg-teal-50 text-teal-600"}`}>
                    <c.icon className="w-5 h-5" />
                  </span>
                  {c.badge && <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-400 text-slate-900 px-2 py-1 rounded-full">{c.badge}</span>}
                </div>
                <h3 className={`font-display font-bold text-xl mt-4 ${c.highlight ? "text-white" : "text-slate-900"}`}>{c.title}</h3>
                <ul className="mt-4 space-y-2">
                  {c.items.map((it) => (
                    <li key={it} className={`flex items-start gap-2 text-sm ${c.highlight ? "text-slate-300" : "text-slate-600"}`}>
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${c.highlight ? "text-emerald-400" : "text-teal-500"}`} />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal className="mt-8">
          <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl px-5 py-4 max-w-3xl">
            <span className="font-semibold text-slate-700">AYOBELL Payroll &amp; Staff Hours</span> prepares approved staff hours and gross-pay information for payroll processing. It is not an HMRC payroll engine.
          </p>
        </Reveal>

        <Reveal className="mt-10" delay={0.05}>
          <Link to="/platform" data-testid="explore-platform-features-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 bg-white border border-teal-200 px-6 py-3.5 rounded-full hover:bg-teal-50 hover:gap-3 transition-all">
            Explore all platform features <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ============ SCREENSHOT SHOWCASE ============ */
function Showcase() {
  const keys = Object.keys(SCREEN_MAP);
  const [active, setActive] = useState(keys[0]);
  const Active = SCREEN_MAP[active].Comp;
  return (
    <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Label>Product tour</Label>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">See AYOBELL in action</h2>
          <p className="mt-4 text-lg text-slate-600">Explore the workflows care teams use every day.</p>
        </Reveal>

        <Reveal className="mt-10 flex flex-wrap justify-center gap-2" delay={0.1}>
          {keys.map((k) => {
            const { label, icon: Icon } = SCREEN_MAP[k];
            const on = k === active;
            return (
              <button
                key={k}
                onClick={() => setActive(k)}
                data-testid={`showcase-tab-${k}`}
                className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-2.5 rounded-full transition-all ${on ? "bg-slate-900 text-white shadow-md" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            );
          })}
        </Reveal>

        <Reveal className="mt-10 max-w-4xl mx-auto" delay={0.15}>
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <BrowserFrame image={SCREEN_IMAGES[active]?.src} alt={SCREEN_IMAGES[active]?.alt}><Active /></BrowserFrame>
            </motion.div>
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
}

/* ============ ROLE-BASED VALUE ============ */
const ROLES = [
  { icon: UserCog, title: "Registered Managers", desc: "Visibility across care, workforce and governance in one place." },
  { icon: HandHeart, title: "Care & Support Workers", desc: "Quick access to the information and workflows needed to deliver care." },
  { icon: LineChart, title: "Operations Teams", desc: "Understand staffing, sites, incidents and operational performance." },
  { icon: ClipboardCheck, title: "Quality & Compliance", desc: "Bring evidence, audits, actions and readiness indicators together." },
];
function Roles() {
  return (
    <section className="bg-slate-50 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-2xl">
          <Label>For every role</Label>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">Built for your whole care team</h2>
        </Reveal>
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {ROLES.map((r) => (
            <StaggerItem key={r.title}>
              <div className="h-full rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-xl hover:-translate-y-1 transition-all">
                <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 text-white"><r.icon className="w-5 h-5" /></span>
                <h3 className="font-display font-bold text-lg mt-4 text-slate-900">{r.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ============ DATA MIGRATION ============ */
const STEPS = [
  { icon: UploadCloud, label: "Upload", desc: "CSV, XML, JSON & ZIP imports" },
  { icon: MapIcon, label: "Map", desc: "Field mapping with confidence" },
  { icon: FileCheck2, label: "Validate", desc: "Pre-flight checks & rules" },
  { icon: PlayCircle, label: "Simulate", desc: "Migration simulation" },
  { icon: Eye, label: "Review", desc: "Critical issue identification" },
  { icon: Rocket, label: "Migrate", desc: "Scheduled go-live & rollback" },
];
function Migration() {
  return (
    <section id="migration" className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8 scroll-mt-20">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-2xl">
          <Label>Data migration</Label>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">Move to AYOBELL with confidence</h2>
          <p className="mt-4 text-lg text-slate-600">
            The AYOBELL Data Migration Centre helps you transform, map and validate your data — with migrations validated before go-live.
          </p>
        </Reveal>

        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 mt-14">
          {STEPS.map((s, i) => (
            <StaggerItem key={s.label}>
              <div className="relative h-full rounded-2xl border border-slate-200 bg-slate-50 p-5 hover:border-teal-500/40 hover:bg-white hover:shadow-lg transition-all">
                <div className="flex items-center justify-between">
                  <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-teal-50 text-teal-600"><s.icon className="w-5 h-5" /></span>
                  <span className="font-mono text-xs text-slate-300 font-bold">0{i + 1}</span>
                </div>
                <h3 className="font-display font-bold text-base mt-4 text-slate-900">{s.label}</h3>
                <p className="mt-1 text-xs text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal className="mt-8 flex flex-wrap gap-2" delay={0.1}>
          {["Data transformation", "Mapping confidence", "Separation of duties", "Rollback planning", "Migration reporting"].map((t) => (
            <span key={t} className="text-xs font-medium text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full">{t}</span>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

/* ============ GOVERNANCE + SECURITY ============ */
const GOV = ["Training compliance", "Supervisions", "Incidents", "Safeguarding", "Complaints", "Audits", "Action plans", "Governance evidence", "CQC readiness indicators"];
const SEC = [
  { icon: Server, title: "Secure organisation separation", desc: "Each organisation's data is kept separate within a secure multi-tenant architecture." },
  { icon: UserCog, title: "Role-based access", desc: "Admin and manager permissions control access to sensitive care information." },
  { icon: FileCheck2, title: "Audit trails", desc: "Actions are recorded to support accountability and governance." },
  { icon: Lock, title: "Secure authentication", desc: "Controlled, authenticated access across multi-site organisations." },
];
function Governance() {
  return (
    <section id="governance" className="bg-slate-900 text-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8 scroll-mt-20 relative overflow-hidden">
      <div className="absolute inset-0 ayo-grid-bg-dark opacity-40" />
      <div className="absolute -top-20 right-0 w-[30rem] h-[30rem] rounded-full bg-teal-500/10 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-teal-300 bg-white/5 border border-white/10 px-3 py-1 rounded-full">CQC & Governance</span>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4">Be ready for the questions that matter.</h2>
            <p className="mt-4 text-lg text-slate-300">
              AYOBELL helps providers bring operational and governance evidence together — ready when you need it.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {GOV.map((g) => (
                <span key={g} className="text-xs font-medium text-teal-100 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">{g}</span>
              ))}
            </div>
            <p className="mt-6 text-xs text-slate-400 leading-relaxed max-w-lg border-l-2 border-teal-500/50 pl-4">
              AYOBELL's readiness indicators are internal management tools designed to support inspection preparation and continuous improvement. They do not constitute an official CQC rating or guarantee regulatory compliance.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <BrowserFrame image={SCREEN_IMAGES.cqc?.src} alt={SCREEN_IMAGES.cqc?.alt} url="app.ayobell.com/governance">{SCREEN_MAP.cqc.Comp()}</BrowserFrame>
          </Reveal>
        </div>

        <div className="mt-20">
          <Reveal className="max-w-2xl">
            <h3 className="font-display font-bold text-2xl sm:text-3xl">Security built for care data</h3>
            <p className="mt-3 text-slate-300">Security designed around sensitive care information.</p>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {SEC.map((s) => (
              <StaggerItem key={s.title}>
                <div className="h-full rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors">
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-teal-500/15 text-teal-300"><s.icon className="w-5 h-5" /></span>
                  <h4 className="font-semibold text-base mt-4">{s.title}</h4>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}

/* ============ PRICING ============ */
function Pricing() {
  return (
    <section id="pricing" className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8 scroll-mt-20">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <Label>Pricing</Label>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900">Simple pricing that grows with your service.</h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            AYOBELL pricing is based on your organisation size and service requirements. Talk to us about your organisation and we'll build a package that fits.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/pricing" data-testid="pricing-talk-btn" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 px-7 py-4 rounded-full hover:shadow-lg hover:shadow-emerald-500/30 transition-all">Talk to our team <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/contact" data-testid="pricing-request-btn" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-700 bg-slate-100 px-7 py-4 rounded-full hover:bg-slate-200 transition-all">Request Pricing</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============ ABOUT ============ */
function About() {
  return (
    <section id="about" className="bg-slate-50 py-20 sm:py-28 px-4 sm:px-6 lg:px-8 scroll-mt-20">
      <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
        <Reveal>
          <Label>About AYOBELL</Label>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-4 text-slate-900 leading-tight">
            Technology should make care simpler, not create more administration.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-lg text-slate-600 leading-relaxed">
            AYOBELL was created to bring the information care teams rely on into one connected environment — helping organisations improve visibility, consistency and oversight while keeping the person receiving support at the centre.
          </p>
          <p className="mt-4 text-slate-500 leading-relaxed">
            AYOBELL is developed by <span className="font-semibold text-slate-700">TechSpace Solutions Ltd</span>, designed around the practical realities of modern care delivery.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ============ DEMO ============ */
function Demo() {
  return (
    <section id="demo" className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8 scroll-mt-20">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
        <Reveal>
          <Label>Book a demo</Label>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4 text-slate-900 leading-tight">
            See AYOBELL in action for your service.
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Tell us about your organisation and we'll show you how AYOBELL can support your care model, teams and governance processes.
          </p>
          <div className="mt-8 space-y-4">
            {["A personalised walkthrough of the platform", "Tailored to your service type & size", "Guidance on data migration & onboarding"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-teal-600 shrink-0" /> <span className="text-sm">{t}</span>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <DemoForm />
        </Reveal>
      </div>
    </section>
  );
}

/* ============ CONTACT ============ */
function Contact() {
  return (
    <section id="contact" className="bg-slate-50 py-20 px-4 sm:px-6 lg:px-8 scroll-mt-20">
      <div className="max-w-3xl mx-auto text-center">
        <Reveal>
          <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-teal-50 text-teal-600 mx-auto"><Mail className="w-5 h-5" /></span>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-4 text-slate-900">Get in touch</h2>
          <p className="mt-3 text-slate-600">Have a question about AYOBELL? Our team is happy to help.</p>
          <a href="mailto:info@ayobell.com" data-testid="contact-email" className="mt-5 inline-flex items-center gap-2 text-lg font-semibold text-teal-700 hover:text-teal-800 transition-colors">
            info@ayobell.com
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO(
    "AYOBELL — Care Management. Connected. | UK Care Management Software",
    "AYOBELL connects care planning, medication (eMAR), staff rota, payroll & staff hours, governance and CQC readiness in one secure platform for UK supported living, residential and domiciliary care."
  );
  return (
    <>
      <Hero />
      <SectorStrip />
      <ValueBand />
      <Marquee />
      <Platform />
      <Showcase />
      <Roles />
      <Migration />
      <Governance />
      <Pricing />
      <About />
      <Demo />
      <Contact />
    </>
  );
}
